import { Routes, Route, NavLink, Navigate, Link, useLocation } from "react-router-dom";
import { Search, HardDrive, ScanLine, LogOut, Loader2, Users, Settings as SettingsIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "./lib/cn";
import { useAuth } from "./hooks/useAuth";
import LoginPage from "./pages/LoginPage";
import SearchPage from "./pages/SearchPage";
import ScanPage from "./pages/ScanPage";
import DiskListPage from "./pages/DiskListPage";
import DiskDetailPage from "./pages/DiskDetailPage";
import SettingsPage from "./pages/SettingsPage";

const ADMIN_EMAILS_CLIENT = (import.meta.env.VITE_ADMIN_EMAILS ?? "dominic@koloc.studio")
  .split(",")
  .map((e: string) => e.trim().toLowerCase())
  .filter(Boolean);

function isAdminEmail(email: string | undefined): boolean {
  if (!email) return false;
  return ADMIN_EMAILS_CLIENT.includes(email.toLowerCase());
}

export default function App(): JSX.Element {
  const { session, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-foreground/40" />
      </div>
    );
  }

  if (!session) return <LoginPage />;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopBar />
      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] min-h-[calc(100vh-60px)]">
        <Sidebar />
        <main className="px-7 md:px-10 pb-20 min-w-0">
          <SubNav />
          <Routes>
            <Route path="/" element={<Navigate to="/recherche" replace />} />
            <Route path="/recherche" element={<SearchPage />} />
            <Route path="/scan" element={<ScanPage />} />
            <Route path="/disques" element={<DiskListPage />} />
            <Route path="/disques/:id" element={<DiskDetailPage />} />
            <Route path="/pigistes" element={<PigistesPlaceholder />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function TopBar(): JSX.Element {
  const { session, signOut } = useAuth();
  const email = session?.user?.email ?? "";
  const initials = email ? email[0]!.toUpperCase() : "?";
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const close = (): void => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);

  return (
    <header
      className="sticky top-0 z-40 h-[60px] flex items-center justify-between px-5 md:px-8 border-b"
      style={{
        background: "rgba(8,9,13,0.92)",
        backdropFilter: "saturate(160%) blur(34px)",
        WebkitBackdropFilter: "saturate(160%) blur(34px)",
        borderColor: "hsl(var(--foreground) / 0.06)",
      }}
    >
      <Link to="/" className="flex items-center gap-3 no-underline text-foreground">
        <span
          className="grid place-items-center flex-shrink-0"
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            background: "#fafafa",
            boxShadow: "0 1px 2px rgba(0,0,0,0.4), inset 0 0.5px 0 rgba(255,255,255,0.6)",
          }}
        >
          <svg viewBox="0 38 82 114" width="17" height="23" aria-hidden="true">
            <path
              fill="#0a0a0a"
              d="M46.61,67.16c10.19-8.9,20.39-18.09,30.59-27.58l1.08,30.17c0,1.44-.07,2.87-.22,4.3-.14,1.3-.36,2.6-.64,3.88-.24,1.18-.76,2.29-1.51,3.23-2.3,3.16-6.11,5.24-11.42,6.25-.29,0-1.97,.4-5.06,1.19-3.1,.79-6.68,1.69-10.78,2.69s-8.37,2.08-12.82,3.23-8.26,2.08-11.41,2.8l33.82,.86c6.75,.29,11.77,1.98,15.08,5.07,3.36,3.18,5.65,7.32,6.57,11.85,1.13,5.42,1.46,10.97,1,16.48-.43,6.17-.93,12.5-1.51,19H44c1.05-4.08,1.84-8.21,2.37-12.39,.41-3.22,.59-6.46,.54-9.7-.04-2.47-.37-4.93-1-7.32-.47-1.83-1.11-3.61-1.91-5.32-1.38-3.22-3.81-5.88-6.89-7.54-.96-.4-1.97-.69-3-.86-1.44-.29-2.94-.57-4.52-.86s-3.2-.54-4.85-.76c-1.39-.19-2.79-.3-4.2-.32,1,1.29,2.58,3.63,4.74,7,2.34,3.7,4.46,7.54,6.35,11.49,2.08,4.31,3.71,8.82,4.85,13.46,1.14,4.25,1.21,8.71,.22,13H.72l-.22-110.66c4.74,0,9.34-.07,13.79-.22h20.71c3.1-.11,6.06,1.25,8,3.67,1.87,2.15,2.49,4.23,1.84,6.24-.85,2.32-2.22,4.42-4,6.14-2.31,2.37-4.76,4.6-7.33,6.68-2.92,2.43-5.62,5.1-8.07,8-2.54,2.99-4.65,6.33-6.25,9.91-1.72,3.96-2.44,8.28-2.1,12.58,9.48-8.19,19.32-16.73,30.42-25.64Z"
            />
          </svg>
        </span>
        <div className="flex flex-col leading-tight">
          <span className="font-mono text-[11px] tracking-[0.22em] uppercase font-medium">Koloc Tools</span>
          <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-foreground/40">Outils internes</span>
        </div>
      </Link>

      <div className="relative">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          className="grid h-8 w-8 place-items-center rounded-full bg-foreground/10 text-foreground text-[12px] font-semibold uppercase border border-foreground/12 transition hover:bg-foreground/15"
          aria-label="Menu utilisateur"
        >
          {initials}
        </button>
        {open && (
          <div
            className="absolute right-0 mt-2 w-64 overflow-hidden glass rounded-md animate-slide-up"
            style={{ borderColor: "hsl(var(--foreground) / 0.12)" }}
          >
            <div className="px-4 py-3 border-b border-foreground/8">
              <div className="label-caps">Connecté</div>
              <div className="mt-1 truncate text-[13px]">{email}</div>
            </div>
            <button
              onClick={() => {
                void signOut();
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 px-4 py-3 text-[13px] hover:bg-foreground/5 transition-colors text-left"
            >
              <LogOut className="h-3.5 w-3.5" />
              Se déconnecter
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

function Sidebar(): JSX.Element {
  const { session } = useAuth();
  const { pathname } = useLocation();
  const isDiskTool =
    pathname.startsWith("/recherche") ||
    pathname.startsWith("/scan") ||
    pathname.startsWith("/disques") ||
    pathname === "/";
  const isPigistes = pathname.startsWith("/pigistes");
  const isSettings = pathname.startsWith("/settings");
  const showSettings = isAdminEmail(session?.user?.email);

  return (
    <aside
      className="hidden md:flex md:flex-col md:sticky md:top-[60px] md:h-[calc(100vh-60px)] glass"
      style={{
        borderRight: "1px solid hsl(var(--foreground) / 0.06)",
        padding: "20px 14px",
      }}
    >
      <div className="px-3 pb-3 label-caps">Outils</div>
      <SidebarItem to="/recherche" icon={<HardDrive className="h-3.5 w-3.5" />} active={isDiskTool}>
        Disk index
      </SidebarItem>
      <SidebarItem to="/pigistes" icon={<Users className="h-3.5 w-3.5" />} active={isPigistes}>
        Répertoire Pigistes
      </SidebarItem>
      {showSettings && (
        <>
          <div className="px-3 pt-6 pb-3 label-caps">Admin</div>
          <SidebarItem to="/settings" icon={<SettingsIcon className="h-3.5 w-3.5" />} active={isSettings}>
            Settings
          </SidebarItem>
        </>
      )}
    </aside>
  );
}

function SidebarItem({
  to,
  icon,
  children,
  active,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  active: boolean;
}): JSX.Element {
  return (
    <Link
      to={to}
      className={cn(
        "relative flex items-center gap-3 rounded-md px-3 py-2.5 text-[13.5px] font-medium transition-colors",
        active
          ? "bg-foreground/10 text-foreground"
          : "text-foreground/70 hover:bg-foreground/5 hover:text-foreground"
      )}
    >
      {active && (
        <span
          className="absolute"
          style={{
            left: -14,
            top: 9,
            bottom: 9,
            width: 2,
            borderRadius: "0 2px 2px 0",
            background: "hsl(var(--foreground))",
          }}
        />
      )}
      <span className={cn("flex-shrink-0", active ? "opacity-100" : "opacity-75")}>{icon}</span>
      <span>{children}</span>
    </Link>
  );
}

function SubNav(): JSX.Element {
  const { pathname } = useLocation();
  if (pathname.startsWith("/pigistes") || pathname.startsWith("/settings")) return <></>;
  return (
    <nav className="flex items-center gap-1 pt-7 mb-2 overflow-x-auto">
      <SubNavItem to="/recherche" icon={<Search className="h-3.5 w-3.5" />}>
        Recherche
      </SubNavItem>
      <SubNavItem to="/scan" icon={<ScanLine className="h-3.5 w-3.5" />}>
        Scanner
      </SubNavItem>
      <SubNavItem to="/disques" icon={<HardDrive className="h-3.5 w-3.5" />}>
        Disques
      </SubNavItem>
    </nav>
  );
}

function SubNavItem({
  to,
  icon,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          "inline-flex items-center gap-2 rounded-pill px-4 py-1.5 text-[12.5px] font-medium transition-all",
          isActive
            ? "bg-foreground/10 text-foreground border border-foreground/14"
            : "text-foreground/65 border border-transparent hover:bg-foreground/5 hover:text-foreground"
        )
      }
    >
      {icon}
      {children}
    </NavLink>
  );
}

function PigistesPlaceholder(): JSX.Element {
  return (
    <div className="card-koloc text-center py-16 mt-8">
      <h2 className="text-h2 mb-2 title-dual">Bientôt disponible</h2>
      <p className="text-foreground/55 max-w-md mx-auto">
        Le répertoire des pigistes (DA, stylistes, photographes, monteurs…) sera intégré ici prochainement.
      </p>
    </div>
  );
}

function NotFound(): JSX.Element {
  return (
    <div className="card-koloc text-center mt-8">
      <h2 className="text-h2 mb-2 title-dual">Page introuvable</h2>
      <p className="text-foreground/55">Cette route n'existe pas.</p>
    </div>
  );
}
